import { isEmpty, isNil } from 'lodash';
import type { ZodSchema } from 'zod';
import { z } from 'zod';
import { strongEntries } from './object';

export type NonEmpty<T> = T & { __nonEmptyBrand: 'non-empty' };

export const isNonNil = <T>(x: T): x is Exclude<T, null | undefined> => !isNil(x);
function isNonEmptyItem(
  v: unknown,
): v is unknown[] | Record<string, unknown> | string | number | boolean {
  return typeof v === 'object' || Array.isArray(v) ? !isEmpty(v) : !isNil(v);
}

function isNonEmptyTuple(
  tuple: [string, unknown],
): tuple is [string, unknown[] | Record<string, unknown> | string | number | boolean] {
  const [, v] = tuple;
  return isNonEmptyItem(v);
}

/**
 * Recursively drop all instances of null, undefined, [], and {}
 */
export function removeEmptyObj(obj: Record<string, unknown>): Record<string, unknown> {
  const nonEmptyEntries = strongEntries(obj).filter(isNonEmptyTuple);
  const recursed = nonEmptyEntries.map(([k, v]) =>
    Array.isArray(v)
      ? ([k, removeEmptyArr(v)] as const)
      : typeof v === 'object'
        ? ([k, removeEmptyObj(v)] as const)
        : ([k, v] as const),
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return Object.fromEntries(recursed);
}

/**
 * Recursively drop all instances of null, undefined, [], and {}
 */
export function removeEmptyArr(array: unknown[]): unknown[] {
  const nonEmptyElements = array.filter(isNonEmptyItem);
  return nonEmptyElements.map((v) =>
    Array.isArray(v) ? removeEmptyArr(v) : typeof v === 'object' ? removeEmptyObj(v) : v,
  );
}
export type NonEmptyArray<T> = [T, ...T[]];
export type NonEmptyOf<T extends Array<unknown>> =
  T extends Array<infer U> ? NonEmptyArray<U> : never;
export const assertNonEmpty = <T>(arr: T[], message?: string): NonEmptyArray<T> => {
  if (!isNonEmpty(arr)) {
    throw new Error(message ?? 'Expected non empty array');
  }
  return arr;
};

export const zodNonEmptyArray = <T extends ZodSchema>(t: T) =>
  z.array(t).refine(isNonEmptyArray, 'Expected non empty array');

export function isNonEmptyArray<T>(arr: T[] | null | undefined): arr is NonEmptyArray<T> {
  return Array.isArray(arr) && arr.length > 0;
}

export function take<T, N extends number>(
  arr: NonEmptyArray<T>,
  n: 0 extends N ? never : N,
): NonEmptyArray<T> {
  return arr.slice(0, n) as NonEmptyArray<T>;
}

export function isNonEmpty<T extends string>(
  v: T | undefined | null,
): v is NonEmpty<Exclude<T, undefined>>;
export function isNonEmpty<T>(arr: T[] | null | undefined): arr is NonEmptyArray<T>;
export function isNonEmpty(v: unknown[] | string | null | undefined) {
  if (Array.isArray(v)) {
    return v.length > 0;
  } else {
    return !isNil(v) && v.trim() !== '';
  }
}
